/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import React from 'react'
import { Link, RouteComponentProps, Route } from 'react-router-dom'
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory, { PaginationProvider, PaginationListStandalone, SizePerPageDropdownStandalone, PaginationTotalStandalone } from 'react-bootstrap-table2-paginator'
import filterFactory from 'react-bootstrap-table2-filter'
import './AccountFacilities.scss'
import { injectProperty, container } from '../../arc/configuration/ServiceLocator'
import { IAccountFacilityApiClient } from '../../services/AccountFacilityApiClient/contracts'
import { observer, Observer } from 'mobx-react'
import { IAccountFacilityGridStateManager, IAccountFacilityGrid } from '../../services/AccountFacilityStateManager/contracts'
import overlayFactory from 'react-bootstrap-table2-overlay'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faSearch } from '@fortawesome/free-solid-svg-icons'
import { Spinner, Row, InputGroup, Col, Input, Button } from 'reactstrap'
import { TopPart } from '../TopPart/TopPart'

export interface IAccountFacilitiesProps extends RouteComponentProps {
	AccountFacilityStateManager: IAccountFacilityGridStateManager
}

export interface IAccountFacilityStateManagerGridState {
	typingTimeout: any
	isLoading: boolean
}

@observer
export class AccountFacilities extends React.Component<IAccountFacilitiesProps, IAccountFacilityStateManagerGridState> {
	@injectProperty('IAccountFacilityApiClient')
	public readonly FacilityApiClient!: IAccountFacilityApiClient

	private searchTextboxRef: React.RefObject<HTMLInputElement>

	constructor(props: Readonly<any>) {
		super(props)
		this.state = {
			typingTimeout: null,
			isLoading: false,
		}
		this.searchTextboxRef = React.createRef()
	}

	NoDataIndication = (): any => <div className="no-data">{this.state.isLoading ? <Spinner animation="border" role="status" /> : <span>No result found</span>}</div>

	componentDidMount(): void {
		if (this.searchTextboxRef.current != null) {
			this.searchTextboxRef.current.value = this.props.AccountFacilityStateManager.itemsGridCriteria.searchTerm
		}
		this.props.AccountFacilityStateManager.getAuthorizationStatus()

		window.scrollTo(0, 0)
	}

	/**
	 * called any time paging, sorting, page size change changes
	 *
	 * @memberof Facilities
	 */
	private handleTableChange = (type: string, args: any): void => {
		this.setState({ isLoading: true })
		this.props.AccountFacilityStateManager.setItemsGridCriteria({
			sortOrder: args.sortOrder ? args.sortOrder : this.props.AccountFacilityStateManager.itemsGridCriteria.sortOrder,
			sortField: args.sortField ? args.sortField : this.props.AccountFacilityStateManager.itemsGridCriteria.sortField,
			page: args.page,
			sizePerPage: args.sizePerPage,
			searchTerm: this.props.AccountFacilityStateManager.itemsGridCriteria.searchTerm,
		})
			.then(() => {
				this.setState({ isLoading: false })
			})
			.catch(error => {
				this.setState({ isLoading: false })
				alert(error)
			})
	}

	private handleSearch(event: any): Promise<void> {
		event.preventDefault()
		const searchTerm = event.target.value
		if (this.state.typingTimeout) {
			clearTimeout(this.state.typingTimeout)
		}
		return new Promise(resolve => {
			this.setState({
				typingTimeout: setTimeout(() => {
					resolve(this.props.AccountFacilityStateManager.setItemsGridCriteria({ ...this.props.AccountFacilityStateManager.itemsGridCriteria, page: 1, searchTerm: searchTerm }).catch(error => alert(error)))
				}, 300),
			})
		})
	}

	/**
	 * redirects to the facilityDetail view with an id of 0 which prompts the new item mode
	 *
	 * @param {*} event
	 * @memberof IFacilityGrid
	 */
	private newItem(event: any): void {
		event.preventDefault()
		this.props.history.push('/facilities/0')
	}

	render(): React.ReactNode {
		if (this.props.AccountFacilityStateManager.authorizedForFacility) {
			return (
				<div>
					<TopPart pagesource="facilities" />
					<div className="row tab-content">
						<div className="items-grid col">
							<div className="Facilities-grid">
								<div>
									<div className="macAdmin">
										<Button color="primary" className="btn btn-primary lnkAddAccountNpi gaJavascriptLink pull-right btnClass" onClick={(e): void => this.newItem(e)}>
											{' '}
											+&nbsp; Add Facility{' '}
										</Button>
									</div>
								</div>
							</div>
							<div className="results">
								<Row form>
									<Col md={9}>
										<InputGroup>
											<Input onChange={(e): Promise<void> => this.handleSearch(e)} type="text" id="searchFilter" placeholder="Search by Facility or NPI" innerRef={this.searchTextboxRef} />
											<button className="input-group-addon btn btn-primary search">
												<FontAwesomeIcon icon={faSearch} />
											</button>
										</InputGroup>
									</Col>
									<Col md={2}></Col>
								</Row>
							</div>
							<br />
							<hr />
							<PaginationProvider
								pagination={paginationFactory({
									custom: true,
									page: this.props.AccountFacilityStateManager.itemsGridCriteria.page,
									sizePerPage: this.props.AccountFacilityStateManager.itemsGridCriteria.sizePerPage,
									totalSize: this.props.AccountFacilityStateManager.itemsGridResults.total,
									showTotal: true,
									sizePerPageList: [
										{
											text: '10',
											value: 10,
										},
										{
											text: '25',
											value: 25,
										},
										{
											text: '50',
											value: 50,
										},
										{
											text: '100',
											value: 100,
										},
									],
									paginationTotalRenderer: (from: number, to: number, size: number) => (
										<span className="react-bootstrap-table-pagination-total">
											{' '}
											Showing {from} to {to} of {size} Results{' '}
										</span>
									),
								})}
							>
								{({ paginationProps, paginationTableProps }: any): any => {
									return (
										<div>
											<div style={{ marginBottom: this.props.AccountFacilityStateManager.itemsGridResults.total === 0 ? '20px' : '' }} className="clearfix">
												<span className="float-left">
													<SizePerPageDropdownStandalone {...paginationProps} />
												</span>
												<span id="page">
													<PaginationTotalStandalone {...paginationProps} />
												</span>
												<span className="float-right">
													<PaginationListStandalone {...paginationProps} />
												</span>
											</div>
											<Observer>
												{(): any => (
													<BootstrapTable
														keyField="id"
														classes="dataTable"
														bootstrap4
														hover
														condensed
														remote
														noDataIndication={(): JSX.Element => <this.NoDataIndication />}
														onTableChange={(type: string, args: any): any => this.handleTableChange(type, args)}
														overlay={overlayFactory({ spinner: true, styles: { overlay: (base: any): any => ({ ...base, background: 'rgba(255, 0, 0, 0.5)' }) } })}
														data={this.props.AccountFacilityStateManager.itemsGridResults.items.slice()}
														defaultSorted={[
															{
																dataField: this.props.AccountFacilityStateManager.itemsGridCriteria.sortField,
																order: this.props.AccountFacilityStateManager.itemsGridCriteria.sortOrder,
															},
														]}
														filter={filterFactory()}
														{...paginationTableProps}
														columns={[
															{
																dataField: 'facilityName',
																text: 'Facility Name',
																sort: true,
																headerStyle: (): Record<string, string> => {
																	return { width: '70%' }
																},
															},
															{
																dataField: 'id',
																text: 'Options',
																sort: false,
																headerStyle: (): Record<string, string> => {
																	return { width: '30%' }
																},
																formatter: (cell: JSX.Element, row: IAccountFacilityGrid): JSX.Element => {
																	return (
																		<div>
																			<FontAwesomeIcon icon={faEdit} />
																			<Link to={`/facilities/${row.id}`}>edit</Link>
																		</div>
																	)
																},
															},
														]}
													/>
												)}
											</Observer>
										</div>
									)
								}}
							</PaginationProvider>
						</div>
					</div>
				</div>
			)
		} else {
			return (
				<div>
					<TopPart pagesource="facilities" />
					<div className="row  tab-content">
						<div className="width100P">
							<div className="contentBody">
								<div className="col-md-6 alert alert-error">You do not have access to this feature. Please contact your administrator.</div>
							</div>
						</div>
					</div>
				</div>
			)
		}
	}
}

export const AccountFacilitiesConnected = (): any => {
	const store = container.get<IAccountFacilityGridStateManager>('IAccountFacilityGridStateManager')
	return <Route exact path="/facilities" render={(props): JSX.Element => <AccountFacilities {...props} AccountFacilityStateManager={store} />} />
}
